'use strict';

import {isEmpty, v} from "@uw-it-sis/lib-react/lib/Utils";
import Level from "@uw-it-sis/lib-react/lib/FeedbackMessagesLevel";
import feedbackMessagesStore from "@uw-it-sis/lib-react/lib/FeedbackMessagesStore";
import termStore from '@uw-it-sis/lib-react/lib/TermStore';
import courseSummaryStore from './CourseSummaryStore';
import {States} from '@uw-it-sis/lib-react/lib/AppConstants';
import {getAggregateState} from '@uw-it-sis/lib-react/lib/StateUtils';
import planItemStore from './PlanItemStore'
import { flow, action, observable, configure, observe, makeObservable } from "mobx";
configure({enforceActions: 'always'});

/**
 * Store for the add course to plan form.
 */
class AddCourseToPlanFormStore {

    state = States.initial;
    // This state is by the button area at the bottom of the widget.
    actionState = States.initial;
    formData = {
        "code": "",
        "termId": "",
        "note": "",
        "backup": false
    };

    constructor() {
        makeObservable(this, {
            state: observable,
            actionState: observable,
            formData: observable,
            resolvePlanItemsAction: action,
            checkDataLoadStates: action,
            updateFormData: action
        });

        this.baseUrl = 'api/terms';
        // This is the object to track the status of the loads.
        this.dataLoadStates = {
            futureTerms: States.initial,
            courseSummary: States.initial
        };
    }

    /**
     * Initialize the data.
     */
    mount = flow(function* (course) {
        this.course = course;

        /*
         * Setup state observers for stores which manage data on which this store depends.
         */
        this.termStoreDisposer = observe(termStore, "state", (change) => {
            // Set the data state for this store to the new value.
            this.dataLoadStates.futureTerms = change.newValue;
            // If the data load was successful then set the term Id to the first item in the list.
            if (change.newValue === States.done) {
                this.formData.termId = termStore.get('futureTerms')[0].id;
            }
            // Precipitate a UI update if the data loads have completed.
            this.checkDataLoadStates();
        });

        this.courseSummaryStoreDisposer = observe(courseSummaryStore, "state", (change) => {
            // Set the data state for this store to the new value.
            this.dataLoadStates.courseSummary = change.newValue;
             // Precipitate a UI update if the data loads have completed.
            this.checkDataLoadStates();
        });

        /**
         * Setup state observer for plan item CRUD actions store.
         */
        this.planItemsStoreDisposer = observe(planItemStore, "state", (change) => {
            // There are other new states but only these are actionable.
            switch (change.newValue) {
                case States.error:
                    this.resolvePlanItemsAction(States.error, planItemStore.getErrorMessage());
                    break;
                default:
                    this.resolvePlanItemsAction(change.newValue);
            }
        });

        this.state = States.pending;

        // Set the course code in the form data since we know it at this point.
        this.formData.code = this.course.code;

        // Don't yield for these. The observers defined in the constructor will resolve them.
        termStore.load('futureTerms');
        courseSummaryStore.loadCourseSummary(this.course.code, this.course.courseId);
    }.bind(this));

    unmount() {
        this.planItemsStoreDisposer();
        this.courseSummaryStoreDisposer();
        this.termStoreDisposer()
    }

    /**
     * Update observables when a plan item action completes.
     * @param newState The state that the request completed in: done or error.
     * @param errorMessage If state is error then this is the accompanying error message.
     */
    resolvePlanItemsAction(newState, errorMessage) {
        this.actionState = newState;
        if (newState === States.error) {
            this.actionErrorMessage = errorMessage;
        }
    }

    /**
     * Update state if necessary.
     */
    checkDataLoadStates() {
        this.state = getAggregateState(this.dataLoadStates);
    }

    /**
     * Allows inputs on the form to communicate their values as they are changed.
     */
    updateFormData(property, newValue) {
        this.formData[property] = newValue;
    }

    /*
     * Getter for the UI to look at form data.
     */
    getFormData() {
        return this.formData;
    }

    /**
     * Validate the form that was passed in from the form.
     *
     * Note: These validation messages land in the feedback messages area of the page, so they aren't particularly useful
     * except for debugging. Also, the form has all data supplied by default, so it naturally isn't inclined toward
     * errors.
     */
    _isRequestValid() {
        let rVal = true;

        if ( ! this.formData ) {
            feedbackMessagesStore.addMessage({"level": Level.ERROR, "text": "Form data object was missing."});
            //  Bail out if we don't have any of the form data.
            return false;
        }

        if ( ! v("code", this.formData) ) {
            feedbackMessagesStore.addMessage({"level": Level.ERROR, "text": "Course Code property was missing."});
            rVal = false;
        }

        if ( ! v("termId", this.formData) ) {
            feedbackMessagesStore.addMessage({"level": Level.ERROR, "text": "Term Id property was missing."});
            rVal = false;
        }

        //  It's okay for this one to be empty, but not undefined.
        if ( v("note", this.formData) === undefined ) {
            feedbackMessagesStore.addMessage({"level": Level.ERROR, "text": "Note property was missing."});
            rVal = false;
        }

        if ( v("backup", this.formData) === undefined ) {
            feedbackMessagesStore.addMessage({"level": Level.ERROR, "text": "The 'is backup' flag was missing."});
            rVal = false;
        }
        return rVal;
    }

    /**
     * Performs form validation and attempts to add the course using PlanItemsStore.
     */
    addCourse = flow(function* () {
        this.actionState = States.pending;

        if (! this._isRequestValid()) {
            this.actionState = States.initial;
            return;
        }

        const courseCode = this.formData.code;
        const termId = this.formData.termId;
        const note =  this.formData.note;
        const isBackup = this.formData.backup;

        // The observer will resolve this request.
        planItemStore.add(courseCode, termId, note, isBackup);
    }.bind(this));
}

export default AddCourseToPlanFormStore;